import React, { useState, useMemo, memo } from "react";
import styles from "./chartCompare.module.scss";
import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";
import { formatItalianNumber } from "utils/formatUtils";
import { useComparisonLabels } from "../ComparisonLabelsContext";

type ChartCompareProps = {
  data: any;
  data2: any;
  title: string;
};

const ChartCompareComponent: React.FC<ChartCompareProps> = ({
  data,
  data2,
  title = "",
}) => {
  useTranslation();
  const labels = useComparisonLabels();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [columns] = useState(3);
  const totalRows = data.length + 1;
  const rows = [];

  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    const row = rowIndex + 1;

    rows.push(row);
  }
  const cells = useMemo(() => {
    const generatedCells = [];
    for (const row of rows) {
      for (let colIndex = 0; colIndex < columns; colIndex++) {
        const cell = {
          row,
          col: colIndex + 1,
        };

        generatedCells.push(cell);
      }
    }

    return generatedCells;
  }, [rows, columns]);
  const dashValue = (dataValue: any): any => {
    if (dataValue === null || dataValue === undefined || typeof dataValue !== 'number' || isNaN(dataValue)) {
      return "-";
    }
    return formatItalianNumber(dataValue);
  };

  const renderedCells = useMemo(() => {
    return cells.map((cell) => {
      const { row, col } = cell;

      if (row === 1) {
        return (
          <div key={`${row}-${col}`}>
            <div className={styles.firstTitle}>{col === 1 && title}</div>
            <div
              className={[styles.firstTitle, styles.firstTitlePortafoglio].join(
                " ",
              )}
            >
              {col === 2 && labels.portfolio1Perc}
            </div>
            <div
              className={[styles.firstTitle, styles.firstTitlePortafoglio].join(
                " ",
              )}
            >
              {col === 3 && labels.portfolio2Perc}
            </div>
          </div>
        );
      }
      if (row > 1) {
        if (col === 1 && row > 1) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                styles.cellKeys,
                row === totalRows
                  ? styles.cellWithNoBorder
                  : styles.cellWithBorder,
              ].join(" ")}
            >
              {row > 1 && data[row - 2].label}
            </div>
          );
        }

        if (col > 1 && row > 1) {
          return (
            <div key={`${row}-${col}`}>
              <div className={styles.cellValueInnerHeader}>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                    }}
                  >
                    <div
                      className={[
                        styles.cellValueInnerHeaderValue,
                        row === totalRows
                          ? styles.cellWithNoBorder
                          : styles.cellWithBorder,
                        col < 3 ? styles.cellRightBorder : null,
                      ].join(" ")}
                      style={{
                        textAlign: "center",
                        lineHeight: "40px",
                        color: colorsConfig.FinecoBlack,
                      }}
                    >
                      {row === totalRows ? "" : ""}
                      {col === 2 && dashValue(data[row - 2].weight)}
                      {col === 3 && dashValue(data2[row - 2].weight)}
                      {/* {col === 4 && dashValue(outputData[row - 2].sharpe)} */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
      }

      return (
        <div key={`${row}-${col}`} className={styles.cellValue}>
          -
        </div>
      );
    });
  }, [cells, data]);

  return (
    <div className={styles.container}>
      <div className={`${styles.grid} ${styles[`grid${columns}`]}`}>
        {renderedCells}
      </div>
    </div>
  );
};

export const ChartCompare = memo(
  ChartCompareComponent,
  (prevProps, nextProps) => {
    return JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);
  },
);

export default ChartCompare;
